import { createContext, useContext, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import maplibregl, { type LngLatLike, type Map as MlMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useTheme } from '../theme/theme';
import { mapStyle } from './mapStyle';
import type { LatLng } from '../lib/types';
import { lengths, pointAt, project, type Line } from '../lib/polyline';
import { useLayout } from '../lib/layout';
import { useSheetInset } from '../lib/sheetInset';

const MapCtx = createContext<MlMap | null>(null);
export const useMapInstance = () => useContext(MapCtx);
/** Lets markers and lines be rendered anywhere under a map created elsewhere on the screen (live ride). */
export const MapProvider = MapCtx.Provider;

/**
 * Wide screens (tablet, desktop): one map for the whole session, owned by the shell. Screens attach their markers,
 * lines and framing to it instead of making their own, and `inset` is the part of it the side panel covers.
 */
export type ShellMap = { map: MlMap | null; inset: { left: number; top: number; right: number; bottom: number } };
const ShellCtx = createContext<ShellMap | null>(null);
export const ShellMapProvider = ShellCtx.Provider;
export const useShellMap = () => useContext(ShellCtx);

const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * What to frame. Padding is on top of the shell's inset (panel / sheet). `hold`: when only the inset changes (the
 * sheet snapped), keep the spot in the middle where it is instead of framing the points again (the pickup pin).
 */
export type FitTarget = { points: LatLng[]; padding: { top: number; bottom: number; left: number; right: number }; maxZoom?: number; hold?: boolean };

type MapProps = {
  center: LatLng;
  zoom?: number;
  fit?: FitTarget | null;
  style?: CSSProperties;
  children?: ReactNode;
  onMoveEnd?: (c: LatLng) => void;
  onMoveStart?: () => void;
  interactive?: boolean;
  label: string;
  /** the map instance once it exists (for screens that draw on it from elsewhere) */
  onMap?: (m: MlMap | null) => void;
};

/** Create a MapLibre map in `el` styled like the mockup maps; the style follows the theme. */
export function useMapLibre(el: RefObject<HTMLDivElement | null>, opts: { center: LatLng; zoom: number; interactive?: boolean }) {
  const [map, setMap] = useState<MlMap | null>(null);
  const { mode } = useTheme();
  useEffect(() => {
    if (!el.current) return;
    let m: MlMap;
    try {
      m = new maplibregl.Map({
        container: el.current,
        style: mapStyle(mode),
        center: [opts.center.lng, opts.center.lat],
        zoom: opts.zoom,
        attributionControl: { compact: false },
        interactive: opts.interactive ?? true,
        dragRotate: false,
        pitchWithRotate: false,
        touchPitch: false,
        fadeDuration: 0,
      });
    } catch {
      return; // no WebGL (old phone, test runner): the screen still works without the map picture
    }
    m.touchZoomRotate?.disableRotation();
    m.getCanvas().style.outline = 'none';
    setMap(m);
    if (import.meta.env.DEV) (window as unknown as { __zrMap?: MlMap }).__zrMap = m; // for local debugging and the e2e checks
    return () => {
      m.remove();
      setMap(null);
    };
    // the map is created once; theme changes are applied below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const styled = useRef(mode);
  useEffect(() => {
    if (!map || styled.current === mode) return;
    styled.current = mode;
    map.setStyle(mapStyle(mode));
  }, [map, mode]);
  return map;
}

/** Rider moves of the map (drag, pinch) — not the camera moves the app makes. */
function useUserMoves(map: MlMap | null, onMoveStart?: () => void, onMoveEnd?: (c: LatLng) => void) {
  const cb = useRef({ onMoveStart, onMoveEnd });
  cb.current = { onMoveStart, onMoveEnd };
  useEffect(() => {
    if (!map) return;
    const start = (e: unknown) => {
      if ((e as { originalEvent?: unknown }).originalEvent) cb.current.onMoveStart?.();
    };
    const end = (e: unknown) => {
      if ((e as { originalEvent?: unknown }).originalEvent || (e as { userMove?: boolean }).userMove) {
        const c = map.getCenter();
        cb.current.onMoveEnd?.({ lat: c.lat, lng: c.lng });
      }
    };
    map.on('movestart', start);
    map.on('moveend', end);
    return () => {
      map.off('movestart', start);
      map.off('moveend', end);
    };
  }, [map]);
}

/**
 * A map styled like the mockup maps. Markers and lines are children (<MapMarker>, <MapLine>).
 * Phones: its own map, placed by `style`. Wide screens: the shell's shared map (the same children and framing).
 */
export function MapView(props: MapProps) {
  const shell = useShellMap();
  return shell ? <AttachedMap {...props} shell={shell} /> : <OwnMap {...props} />;
}

function OwnMap({ center, zoom = 15, fit, style, children, onMoveEnd, onMoveStart, interactive = true, label, onMap }: MapProps) {
  const el = useRef<HTMLDivElement>(null);
  const map = useMapLibre(el, { center, zoom, interactive });
  const cb = useRef(onMap);
  cb.current = onMap;
  useEffect(() => {
    cb.current?.(map);
    return () => cb.current?.(null);
  }, [map]);
  useUserMoves(map, onMoveStart, onMoveEnd);
  useFit(map, fit, zoom);
  return (
    <div role="img" aria-label={label} style={{ position: 'absolute', overflow: 'hidden', background: 'var(--mapBg)', ...style }}>
      <div ref={el} style={{ position: 'absolute', inset: 0 }} />
      <MapCtx.Provider value={map}>{map && children}</MapCtx.Provider>
    </div>
  );
}

function AttachedMap({ center, zoom = 15, fit, children, onMoveEnd, onMoveStart, label, onMap, shell }: MapProps & { shell: ShellMap }) {
  const { map } = shell;
  const cb = useRef(onMap);
  cb.current = onMap;
  useEffect(() => {
    cb.current?.(map);
    return () => cb.current?.(null);
  }, [map]);
  useUserMoves(map, onMoveStart, onMoveEnd);
  // a screen that frames nothing itself (and doesn't hand the map on) still brings its area into view
  const own = !fit && !onMap;
  useFit(map, own ? { points: [center], padding: { top: 0, bottom: 0, left: 0, right: 0 }, maxZoom: zoom } : fit, zoom);
  useEffect(() => {
    map?.getCanvas().setAttribute('aria-label', label);
  }, [map, label]);
  return (
    <MapCtx.Provider value={map}>
      <div className="zr-map-overlay">{map && children}</div>
    </MapCtx.Provider>
  );
}

/**
 * The shell's shared map, on every layout: one map for the whole session. Children see it through `useShellMap()`.
 * `inset` is the part of it the UI covers: the side panel on wide screens, the sheet on phones.
 */
export function ShellMapHost({ center, children }: { center: LatLng; children: ReactNode }) {
  const el = useRef<HTMLDivElement>(null);
  const map = useMapLibre(el, { center, zoom: 14 });
  const L = useLayout();
  const sheet = useSheetInset();
  const bottom = L.wide ? 0 : sheet;
  const inset = useMemo(() => ({ left: L.mapLeft, top: 0, right: 0, bottom }), [L.mapLeft, bottom]);
  const value = useMemo(() => ({ map, inset }), [map, inset]);
  return (
    <ShellCtx.Provider value={value}>
      <div className="zr-shellmap" style={{ background: 'var(--mapBg)' }}>
        <div ref={el} style={{ position: 'absolute', inset: 0 }} />
      </div>
      {children}
    </ShellCtx.Provider>
  );
}

/**
 * Frame points on the map. The first framing is instant; later ones glide (new driver position, a sheet that
 * snapped, the next step), so the camera never jumps. The shell's inset (panel, sheet) is added to the padding, so
 * points are framed in the part of the map the rider can see.
 */
const framed = new WeakSet<MlMap>();
export function useFit(map: MlMap | null, fit: FitTarget | null | undefined, zoom = 15) {
  const shell = useShellMap();
  const inset = shell && shell.map === map ? shell.inset : null;
  const target = fit ? JSON.stringify([fit.points.map((p) => [+p.lat.toFixed(6), +p.lng.toFixed(6)]), fit.padding, fit.maxZoom]) : '';
  const insetKey = inset ? `${inset.left},${inset.top},${inset.right},${inset.bottom}` : '';
  const last = useRef({ target: '', inset: '' });
  useEffect(() => {
    if (!map || !fit) return;
    const onlyInset = last.current.target === target && last.current.inset !== insetKey;
    last.current = { target, inset: insetKey };
    const duration = !framed.has(map) || reducedMotion() ? 0 : onlyInset ? 420 : 700;
    framed.add(map);
    // on a short or narrow map the padding shrinks with it, so the points always fit
    const el = map.getContainer();
    const shrink = (a: number, b: number, room: number) => {
      const k = a + b > room ? room / (a + b) : 1;
      return [Math.floor(a * k), Math.floor(b * k)];
    };
    const p = inset ? { top: fit.padding.top + inset.top, bottom: fit.padding.bottom + inset.bottom, left: fit.padding.left + inset.left, right: fit.padding.right + inset.right } : fit.padding;
    const [top, bottom] = shrink(p.top, p.bottom, Math.max(0, el.clientHeight - 120));
    const [left, right] = shrink(p.left, p.right, Math.max(0, el.clientWidth - 120));
    const padding = { top, bottom, left, right };
    try {
      if (onlyInset && fit.hold) {
        // the spot under the pin stays under it while the free part of the map changes size
        map.easeTo({ center: map.getCenter(), padding, duration });
      } else if (fit.points.length > 1) {
        const b = new maplibregl.LngLatBounds();
        for (const q of fit.points) b.extend([q.lng, q.lat]);
        // MapLibre adds the map's own padding (left by a one-point framing) to fitBounds' padding, so the camera
        // is worked out with none and the padding eases back to zero with the move
        const kept = map.getPadding();
        const none = { top: 0, bottom: 0, left: 0, right: 0 };
        map.setPadding(none);
        const cam = map.cameraForBounds(b, { padding, maxZoom: fit.maxZoom ?? 16 });
        map.setPadding(kept);
        if (cam) map.easeTo({ ...cam, padding: none, duration });
      } else if (fit.points.length === 1) {
        const opts = { center: [fit.points[0].lng, fit.points[0].lat] as [number, number], zoom: fit.maxZoom ?? zoom, padding };
        if (duration) map.easeTo({ ...opts, duration });
        else map.jumpTo(opts);
      }
    } catch (e) {
      if (import.meta.env.DEV) console.warn('fit failed', e);
      /* padding larger than the map (tiny window): keep the camera */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, target, insetKey]);
}

/** Imperatively move the camera (e.g. "use my location"). */
export function useFlyTo() {
  const map = useMapInstance();
  return (to: LatLng, zoom?: number) => map?.easeTo({ center: [to.lng, to.lat], zoom: zoom ?? map.getZoom(), duration: 400 });
}

/**
 * A position that glides to each new value over `ms` instead of jumping (the driver between location updates).
 * Far moves (a different trip) and reduced motion jump straight there.
 */
export function useGlide(at: LatLng, ms = 1600): LatLng {
  const [shown, setShown] = useState(at);
  const cur = useRef(at);
  useEffect(() => {
    const from = cur.current;
    const far = Math.abs(from.lat - at.lat) + Math.abs(from.lng - at.lng) > 0.05;
    if (reducedMotion() || far || (from.lat === at.lat && from.lng === at.lng)) {
      cur.current = at;
      setShown(at);
      return;
    }
    let raf = 0;
    const t0 = performance.now();
    const step = (now: number) => {
      const k = Math.min(1, (now - t0) / ms);
      const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2; // ease in-out
      const p = { lat: from.lat + (at.lat - from.lat) * e, lng: from.lng + (at.lng - from.lng) * e };
      cur.current = p;
      setShown(p);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [at.lat, at.lng, ms]); // eslint-disable-line react-hooks/exhaustive-deps
  return shown;
}

/**
 * The vehicle's position on screen, moving *along* the road line between updates (so it follows the turns instead
 * of cutting across them). Off the line (more than 120 m away) or without one, it glides in a straight line.
 * `line` must be stable between renders (memoise it).
 */
export function useRouteGlide(at: LatLng, line: Line | null, ms = 1600): LatLng {
  const straight = useGlide(at, ms);
  const cum = useMemo(() => (line && line.length > 1 ? lengths(line) : null), [line]);
  const target = useMemo(() => {
    if (!line || !cum) return null;
    const s = project(line, at, cum);
    return s.off < 120 ? s.along : null;
  }, [line, cum, at.lat, at.lng]); // eslint-disable-line react-hooks/exhaustive-deps
  const [along, setAlong] = useState<number | null>(target);
  const cur = useRef<number | null>(target);
  useEffect(() => {
    const from = cur.current;
    if (target == null || from == null || reducedMotion() || Math.abs(target - from) > 3000 || target === from) {
      cur.current = target;
      setAlong(target);
      return;
    }
    let raf = 0;
    const t0 = performance.now();
    const step = (now: number) => {
      const k = Math.min(1, (now - t0) / ms);
      const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
      const v = from + (target - from) * e;
      cur.current = v;
      setAlong(v);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  if (along == null || !line || !cum) return straight;
  return pointAt(line, along, cum);
}

/** A React element pinned to a map position (MapLibre places it with a transform). */
export function MapMarker({ at, children, anchor = 'center', z }: { at: LatLng; children: ReactNode; anchor?: maplibregl.PositionAnchor; z?: number }) {
  const map = useMapInstance();
  const [node] = useState(() => {
    const d = document.createElement('div');
    d.style.pointerEvents = 'none';
    return d;
  });
  const marker = useRef<maplibregl.Marker | null>(null);
  const pos = useRef(at);
  pos.current = at;
  useEffect(() => {
    if (!map) return;
    marker.current = new maplibregl.Marker({ element: node, anchor }).setLngLat([pos.current.lng, pos.current.lat] as LngLatLike).addTo(map);
    if (z != null) node.style.zIndex = String(z);
    return () => {
      marker.current?.remove();
      marker.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, node, anchor]);
  useEffect(() => {
    marker.current?.setLngLat([at.lng, at.lat]);
  }, [at.lat, at.lng]);
  return createPortal(children, node);
}

/**
 * A line on the map (route, driver → pickup). Colours are token values resolved by the caller. New coordinates
 * update the source in place (no layer churn), so a line can follow a gliding marker.
 */
export function MapLine({ id, coords, color, width, casing, casingWidth, dash }: { id: string; coords: [number, number][]; color: string; width: number; casing?: string; casingWidth?: number; dash?: number[] }) {
  const map = useMapInstance();
  const data = useRef<maplibregl.GeoJSONSourceSpecification['data']>(null!);
  data.current = { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords } };
  useEffect(() => {
    if (!map) return;
    const add = () => {
      try {
        if (!map.getSource(id)) {
          map.addSource(id, { type: 'geojson', data: data.current });
          if (casing) map.addLayer({ id: `${id}-case`, type: 'line', source: id, layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': casing, 'line-width': casingWidth ?? width * 2 } });
          map.addLayer({ id, type: 'line', source: id, layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': color, 'line-width': width, ...(dash ? { 'line-dasharray': dash } : {}) } });
        }
      } catch {
        /* style still loading: the next styledata event adds it */
      }
    };
    const onStyle = () => map.isStyleLoaded() && add();
    if (map.isStyleLoaded()) add();
    else map.once('load', add);
    map.on('styledata', onStyle);
    map.on('idle', onStyle);
    return () => {
      map.off('styledata', onStyle);
      map.off('idle', onStyle);
      map.off('load', add);
      try {
        if (map.getLayer(id)) map.removeLayer(id);
        if (map.getLayer(`${id}-case`)) map.removeLayer(`${id}-case`);
        if (map.getSource(id)) map.removeSource(id);
      } catch {
        /* map already gone */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, id, color, width, casing, casingWidth]);
  const key = JSON.stringify(coords);
  useEffect(() => {
    const src = map?.getSource(id) as maplibregl.GeoJSONSource | undefined;
    src?.setData(data.current);
  }, [map, id, key]);
  return null;
}
